/**
 * Expedition quest panel (M5).
 *
 * Builds its own DOM inside `#hud` — index.html and hud.ts are untouched — and renders exactly
 * what the server last said: chain numeral + kind, title, objective, progress and, for a HOLD,
 * a countdown the client runs locally between the server's 1 Hz heartbeats.
 *
 * The server is authoritative for every number here; this class never infers progress.
 */
import { QUEST_KIND_NAMES, questNumeral, type QuestStateMsg } from '@stillsun/shared';
import './quests.css';

/** How long the completion flash holds before the breather line takes over. */
const FLASH_MS = 900;

export class QuestPanel {
  private readonly root: HTMLDivElement;
  private readonly kindEl: HTMLSpanElement;
  private readonly numeralEl: HTMLSpanElement;
  private readonly titleEl: HTMLDivElement;
  private readonly objectiveEl: HTMLDivElement;
  private readonly countEl: HTMLSpanElement;
  private readonly fillEl: HTMLDivElement;
  private readonly clockEl: HTMLSpanElement;
  private readonly nextEl: HTMLDivElement;

  private state: QuestStateMsg | null = null;
  /** Local clocks, seeded from the last server value and ticked down between heartbeats. */
  private timeLeftMs = 0;
  private haveTimer = false;
  private breatherMs = 0;
  private flashUntil = 0;
  /**
   * Who the rung is waiting on, resolved from `QuestStateMsg.handInNpcId` against the map's own
   * `npcs` by the caller — the wire carries the id and nothing else. Empty when nothing is held.
   */
  private handInName = '';
  /** Last rendered strings, so a 60 Hz update only touches the DOM when something changed. */
  private lastKey = '';
  private lastClock = '';
  private lastNext = '';

  constructor(hudRoot: HTMLElement) {
    const root = document.createElement('div');
    root.id = 'questpanel';
    root.innerHTML = `
      <div class="qp-head"><span class="qp-numeral"></span><span class="qp-right"><span class="qp-kind"></span></span></div>
      <div class="qp-body">
        <div class="qp-title"></div>
        <div class="qp-objective"></div>
        <div class="qp-meter">
          <span class="qp-count"></span>
          <div class="qp-bar"><div class="qp-fill"></div></div>
          <span class="qp-clock"></span>
        </div>
      </div>
      <div class="qp-next"></div>`;
    hudRoot.appendChild(root);
    this.root = root;
    this.numeralEl = root.querySelector('.qp-numeral')!;
    this.kindEl = root.querySelector('.qp-kind')!;
    this.titleEl = root.querySelector('.qp-title')!;
    this.objectiveEl = root.querySelector('.qp-objective')!;
    this.countEl = root.querySelector('.qp-count')!;
    this.fillEl = root.querySelector('.qp-fill')!;
    this.clockEl = root.querySelector('.qp-clock')!;
    this.nextEl = root.querySelector('.qp-next')!;
  }

  /**
   * Apply an S2C.QuestState. `handInName` is the person `msg.handInNpcId` points at, looked up in
   * the map data the caller holds; pass '' (the default) when the rung is not waiting on anybody.
   */
  set(msg: QuestStateMsg, handInName = ''): void {
    this.state = msg;
    this.handInName = msg.handInNpcId === undefined ? '' : handInName;
    this.haveTimer = msg.timeLeftMs !== undefined;
    this.timeLeftMs = msg.timeLeftMs ?? 0;
    this.breatherMs = msg.breatherMs ?? 0;
    if (msg.breatherMs === undefined) this.root.classList.remove('breather', 'done');
    this.root.classList.add('on');
    this.render();
  }

  /** An Ev.QuestComplete landed: flash, then the breather line counts the next one in. */
  complete(breatherMs = 5000): void {
    if (!this.state) return;
    this.flashUntil = performance.now() + FLASH_MS;
    this.breatherMs = breatherMs;
    this.root.classList.add('done', 'breather');
    this.render();
  }

  /** Per-frame. `dtMs` is the frame delta in milliseconds. */
  update(dtMs: number): void {
    if (!this.state) return;
    if (this.breatherMs > 0) this.breatherMs = Math.max(0, this.breatherMs - dtMs);
    // A HOLD clock only runs while the squad is actually inside; the server tells us that by
    // sending a fresh state on the pause/resume edge, so run it down only while a breather is not
    // in play and the goal is not already met.
    if (this.haveTimer && this.breatherMs <= 0 && this.state.progress < this.state.goal) {
      this.timeLeftMs = Math.max(0, this.timeLeftMs - dtMs);
    }
    if (this.flashUntil !== 0 && performance.now() >= this.flashUntil) {
      this.flashUntil = 0;
      this.root.classList.remove('done');
    }
    this.render();
  }

  /** Leaving the expedition (returnToHub / a fresh welcome). */
  clear(): void {
    this.state = null;
    this.haveTimer = false;
    this.timeLeftMs = 0;
    this.breatherMs = 0;
    this.flashUntil = 0;
    this.handInName = '';
    this.lastKey = '';
    this.lastClock = '';
    this.lastNext = '';
    this.root.classList.remove('on', 'done', 'breather', 'handin');
  }

  get visible(): boolean {
    return this.state !== null;
  }

  private render(): void {
    const s = this.state;
    if (!s) return;
    const key = `${s.chainIndex}|${s.kind}|${s.title}|${s.objective}|${s.progress}|${s.goal}`;
    if (key !== this.lastKey) {
      this.lastKey = key;
      this.numeralEl.textContent = `QUEST ${questNumeral(s.chainIndex)}`;
      this.kindEl.textContent = QUEST_KIND_NAMES[s.kind] ?? '';
      this.titleEl.textContent = s.title;
      this.objectiveEl.textContent = s.objective;
      // The tracker draws the objective as a diamond checkbox: filled and struck through
      // once the goal is met. The class is the only tell the CSS has.
      this.objectiveEl.classList.toggle('done', s.progress >= s.goal);
      this.countEl.textContent = `${s.progress} / ${s.goal}`;
      this.fillEl.style.width = `${Math.round((Math.min(s.progress, s.goal) / Math.max(1, s.goal)) * 100)}%`;
    }

    const clock = this.haveTimer ? formatClock(this.timeLeftMs) : '';
    if (clock !== this.lastClock) {
      this.lastClock = clock;
      this.clockEl.textContent = clock;
    }

    const breatherOn = this.breatherMs > 0;
    if (breatherOn !== this.root.classList.contains('breather')) {
      this.root.classList.toggle('breather', breatherOn);
    }
    // The bottom line of the tracker says the one thing left to do. During a breather that is
    // "the next one is coming"; while a rung is held it is a person, and a place to walk to. The
    // breather wins the moment it starts, because by then the hand-in has already happened.
    const handInOn = !breatherOn && this.handInName !== '';
    if (handInOn !== this.root.classList.contains('handin')) {
      this.root.classList.toggle('handin', handInOn);
    }
    const next = handInOn
      ? `RETURN TO ${this.handInName.toUpperCase()}`
      : breatherOn
        ? `NEXT QUEST IN ${Math.max(1, Math.ceil(this.breatherMs / 1000))}`
        : '';
    if (next !== this.lastNext) {
      this.lastNext = next;
      this.nextEl.textContent = next;
    }
  }
}

function formatClock(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}
